import {
   CRUD_ACTIONS,
   actionLabel,
   moduleMeta,
   resourceDescription,
   resourceLabel,
} from './catalog';

// ---------------------------------------------------------------------------
// Modelo puro da tela de permissões — sem React, sem HTTP.
//
// Transforma o catálogo PLANO (`GET /permissions`) na visão hierárquica da
// tela (módulo → recurso → colunas CRUD + chips extras) e concentra as
// regras de interação do design (handoff docs/design/design_handoff_
// permissoes_perfil):
//   · conceder QUALQUER ação de um recurso concede o `read` dele junto;
//   · remover o `read` cascateia a remoção de todas as demais do recurso;
//   · toggle de coluna: todas concedidas no módulo → remove; senão concede.
// O backend NÃO impõe essas dependências — são regra de UX desta tela.
// ---------------------------------------------------------------------------

/**
 * @returns {Array<{
 *   key, label, icon, description, order,
 *   permissions: Array,           // todas do módulo
 *   resources: Array<{
 *     key, label, description,
 *     cruds: { read, create, update, delete },  // permissão ou null
 *     extras: Array,              // ações fora do CRUD
 *     permissions: Array,         // todas do recurso
 *   }>
 * }>}
 */
export function buildCatalogView(permissions) {
   const modules = new Map();

   permissions.forEach((perm) => {
      if (!modules.has(perm.module)) {
         const meta = moduleMeta(perm.module);
         modules.set(perm.module, {
            key: perm.module,
            label: meta.label,
            icon: meta.icon,
            description: meta.description,
            order: meta.order,
            permissions: [],
            resourceMap: new Map(),
         });
      }
      const mod = modules.get(perm.module);
      mod.permissions.push(perm);

      if (!mod.resourceMap.has(perm.resource)) {
         mod.resourceMap.set(perm.resource, {
            key: perm.resource,
            label: resourceLabel(perm.resource),
            description: resourceDescription(perm.resource),
            cruds: { read: null, create: null, update: null, delete: null },
            extras: [],
            permissions: [],
         });
      }
      const resource = mod.resourceMap.get(perm.resource);
      resource.permissions.push(perm);
      if (CRUD_ACTIONS.includes(perm.action)) {
         resource.cruds[perm.action] = perm;
      } else {
         resource.extras.push(perm);
      }
   });

   return Array.from(modules.values())
      .map((mod) => ({
         ...mod,
         resources: Array.from(mod.resourceMap.values()).sort((a, b) =>
            a.label.localeCompare(b.label, 'pt-BR')
         ),
         resourceMap: undefined,
      }))
      .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label, 'pt-BR'));
}

/** Contagens de um módulo para o rail e o cabeçalho da matriz. */
export function moduleStats(mod, granted) {
   let grantedCount = 0;
   let sensitiveGranted = 0;
   let sensitiveDenied = 0;
   mod.permissions.forEach((perm) => {
      const isGranted = granted.has(perm.id);
      if (isGranted) grantedCount += 1;
      if (perm.isSensitive) {
         if (isGranted) sensitiveGranted += 1;
         else sensitiveDenied += 1;
      }
   });
   return {
      total: mod.permissions.length,
      granted: grantedCount,
      commonGranted: grantedCount - sensitiveGranted,
      sensitiveGranted,
      sensitiveDenied,
   };
}

/** Toggle de célula/chip com as dependências implícitas do design. */
export function togglePermission(granted, resource, perm) {
   const next = new Set(granted);
   const readPerm = resource.cruds.read;

   if (next.has(perm.id)) {
      next.delete(perm.id);
      // Remover VER derruba todas as demais do recurso (cascata).
      if (readPerm && perm.id === readPerm.id) {
         resource.permissions.forEach((p) => next.delete(p.id));
      }
   } else {
      next.add(perm.id);
      // Conceder qualquer ação concede VER junto (dependência implícita).
      if (readPerm) next.add(readPerm.id);
   }
   return next;
}

/** Permissões de uma ação CRUD em todos os recursos do módulo. */
export function columnPermissions(mod, action) {
   return mod.resources
      .map((resource) => resource.cruds[action])
      .filter(Boolean);
}

/** Toggle de coluna inteira (header VER/CRIAR/EDITAR/EXCLUIR). */
export function toggleColumn(granted, mod, action) {
   const perms = columnPermissions(mod, action);
   if (!perms.length) return granted;

   const next = new Set(granted);
   const allGranted = perms.every((perm) => next.has(perm.id));

   if (allGranted) {
      perms.forEach((perm) => {
         next.delete(perm.id);
         if (action === 'read') {
            // Remover VER em massa cascateia por recurso.
            const resource = mod.resources.find((r) => r.cruds.read?.id === perm.id);
            resource?.permissions.forEach((p) => next.delete(p.id));
         }
      });
   } else {
      perms.forEach((perm) => next.add(perm.id));
      if (action !== 'read') {
         // Conceder em massa aplica a regra do VER recurso a recurso.
         mod.resources.forEach((resource) => {
            if (resource.cruds[action] && resource.cruds.read) {
               next.add(resource.cruds.read.id);
            }
         });
      }
   }
   return next;
}

/** Diff granted × base → lista do painel ALTERAÇÕES e da barra fixa. */
export function computeDiff(base, granted, permissionsById) {
   const changes = [];
   granted.forEach((id) => {
      if (!base.has(id)) changes.push({ type: 'added', perm: permissionsById.get(id) });
   });
   base.forEach((id) => {
      if (!granted.has(id)) changes.push({ type: 'removed', perm: permissionsById.get(id) });
   });
   return changes.filter((change) => change.perm);
}

/** Rótulo "Recurso · Ação" usado no painel de alterações e no de sensíveis. */
export function permissionDisplayName(perm) {
   return `${resourceLabel(perm.resource)} · ${actionLabel(perm.action)}`;
}

/** Busca client-side: casa por recurso, ação, key/slug e rótulo/descrição. */
export function permissionMatches(perm, resource, query) {
   if (!query) return true;
   const q = query.trim().toLowerCase();
   if (!q) return true;
   return (
      resource.label.toLowerCase().includes(q) ||
      resource.description.toLowerCase().includes(q) ||
      actionLabel(perm.action).toLowerCase().includes(q) ||
      perm.key.toLowerCase().includes(q) ||
      (perm.label ?? '').toLowerCase().includes(q)
   );
}

/** A linha do recurso tem algum match? (busca atenua, não esconde) */
export function resourceMatches(resource, query) {
   if (!query) return true;
   return resource.permissions.some((perm) =>
      permissionMatches(perm, resource, query)
   );
}

export function moduleHasMatch(mod, query) {
   if (!query) return true;
   return mod.resources.some((resource) => resourceMatches(resource, query));
}

/** Resumo global: total, concedidas, módulos completos e sem acesso. */
export function catalogSummary(catalogView, granted) {
   let total = 0;
   let grantedCount = 0;
   let completeModules = 0;
   let emptyModules = 0;
   catalogView.forEach((mod) => {
      const stats = moduleStats(mod, granted);
      total += stats.total;
      grantedCount += stats.granted;
      if (stats.total > 0 && stats.granted === stats.total) completeModules += 1;
      if (stats.granted === 0) emptyModules += 1;
   });
   return { total, granted: grantedCount, completeModules, emptyModules };
}

/** Sensíveis concedidas (para o alerta âmbar do resumo). */
export function grantedSensitive(permissions, granted) {
   return permissions.filter((perm) => perm.isSensitive && granted.has(perm.id));
}
